import { useEffect, useRef, useState } from 'react';

/**
 * Run some function on a regular interval. Disable/stop by passing undefined
 * for the polling interval.
 */
export function usePolling(pollFn: () => void, pollingIntervalMs: number | undefined) {
  useEffect(() => {
    // Don't poll when disabled
    if (pollingIntervalMs === undefined) {
      return;
    }

    // Run the function after timer has expired
    const timer = setInterval(pollFn, pollingIntervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [pollFn, pollingIntervalMs]);
}

/**
 * Retry some asynchronous function that has loading/error state (e.g. fetching
 * data from the server) with a backoff that increases the number of seconds
 * between retries as the number of consecutive errors increases. Returns the
 * number of seconds until a retry will be attempted or undefined if not
 * waiting to retry (i.e. when loading or there is no error to retry).
 */
export function useRetryWithBackoff(retryFn: () => void, loading: boolean, error?: Error) {
  const errors = useRef<{ count: number; lastCounted?: Error }>({ count: 0 });

  if (error === undefined) {
    // When function completes without an error, reset
    errors.current = { count: 0, lastCounted: undefined };
  } else if (error !== errors.current.lastCounted) {
    // Whenever we get a new error we haven't seen before, keep track of it
    errors.current = { count: errors.current.count + 1, lastCounted: error };
  }

  // Don't retry while loading, otherwise see how many seconds to wait
  let retrySeconds: number | undefined = undefined;
  if (loading === false) {
    retrySeconds = getRetrySeconds(errors.current.count);
  }

  // Retry after retrySeconds have expired
  useEffect(() => {
    if (retrySeconds === undefined) return;

    const timer = setTimeout(retryFn, retrySeconds * 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [retrySeconds, retryFn]);

  return retrySeconds;
}

// Number of seconds to wait between retries based on the error count
const RETRY_SECONDS = [5, 10, 30, 90, 180];

function getRetrySeconds(errorCount: number): number | undefined {
  if (errorCount <= 0) return undefined;

  const retryIndex = errorCount - 1;

  // Use the values from the array until the index is past the array's
  // length, in which case just use the last value from that point on
  return retryIndex < RETRY_SECONDS.length - 1 ? RETRY_SECONDS[retryIndex] : RETRY_SECONDS[RETRY_SECONDS.length - 1];
}

/**
 * Counts down from the specified number of seconds, one second at a time.
 * Returns the number of seconds left in the countdown. Passing undefined
 * will stop the timer.
 */
export function useCountdown(seconds?: number) {
  // How many seconds the current timer is counting down from
  const timerSeconds = useRef<number | undefined>(undefined);

  const [timerSecondsLeft, setTimerSecondsLeft] = useState(0);
  useEffect(() => {
    if (seconds === undefined) return;

    // Reset state before setting the timer
    timerSeconds.current = seconds;
    setTimerSecondsLeft(seconds);

    // Set an interval that will tick every second and stop at 0
    const timer = setInterval(() => {
      setTimerSecondsLeft((current) => {
        const next = current - 1;
        if (next <= 0) {
          clearInterval(timer);
        }
        return next;
      });
    }, 1000);

    // Reset state to initial state when seconds changes or unmount
    return () => {
      timerSeconds.current = undefined;
      setTimerSecondsLeft(0);
      clearInterval(timer);
    };
  }, [seconds]);

  // Since timer is an effect and runs delayed, return an accurate number
  // of seconds left when seconds param has changed or on first render when
  // the effect hasn't run yet
  if (timerSeconds.current === seconds) {
    return timerSecondsLeft;
  }
  return seconds ?? 0;
}
